import { Controller } from "@hotwired/stimulus"
import { Chart, DoughnutController, BarController, LineController, ArcElement, BarElement, LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend } from "chart.js"

Chart.register(DoughnutController, BarController, LineController, ArcElement, BarElement, LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend)

// "Orto Botanico" — botanical pastel chart palette
const COLORS = [
  "oklch(0.55 0.10 160)",   // sage green
  "oklch(0.68 0.12 155)",   // emerald mint
  "oklch(0.76 0.10 75)",    // soft honey
  "oklch(0.65 0.10 40)",    // muted terracotta
  "oklch(0.65 0.08 290)",   // lavender
  "oklch(0.62 0.09 195)",   // seafoam
  "oklch(0.70 0.08 230)",   // powder blue
  "oklch(0.72 0.08 120)",   // olive pastel
]

const FONT = { family: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }

export default class extends Controller {
  static values = {
    type: String,
    labels: Array,
    datasets: Array,
    stacked: { type: Boolean, default: false }
  }

  connect() {
    const ctx = this.element.getContext("2d")
    const type = this.typeValue

    const datasets = this.datasetsValue.map((ds, i) => ({
      ...ds,
      backgroundColor: ds.backgroundColor || (type === "line" ? "transparent" : COLORS),
      borderColor: type === "doughnut" ? "white" : (ds.borderColor || COLORS[i]),
      borderWidth: type === "doughnut" ? 3 : (type === "line" ? 2 : 0),
      borderRadius: type === "bar" ? 6 : 0,
      maxBarThickness: 48,
      ...(type === "line" ? { tension: ds.tension ?? 0.3, pointRadius: ds.pointRadius ?? 2, spanGaps: false } : {})
    }))

    this.chart = new Chart(ctx, {
      type,
      data: { labels: this.labelsValue, datasets },
      options: this.optionsFor(type)
    })
  }

  optionsFor(type) {
    const base = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: type === "doughnut" ? "bottom" : "top",
          labels: {
            font: { ...FONT, size: 13, weight: 500 },
            padding: 16,
            usePointStyle: true,
            pointStyle: "circle",
            boxWidth: 8,
            boxHeight: 8,
          }
        },
        tooltip: {
          backgroundColor: "oklch(0.25 0.02 160)",
          titleFont: { ...FONT, size: 13, weight: 600 },
          bodyFont: { ...FONT, size: 13 },
          padding: 12,
          cornerRadius: 8,
          displayColors: true,
        }
      }
    }

    if (type === "doughnut") {
      base.cutout = "65%"
    }

    if (type === "bar" || type === "line") {
      const stacked = this.stackedValue
      base.scales = {
        x: {
          grid: { display: false },
          ticks: { font: { ...FONT, size: 12 } },
          stacked
        },
        y: {
          grid: { color: "oklch(0.92 0.015 155)" },
          ticks: {
            font: { ...FONT, size: 12 },
            callback: (v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v
          },
          beginAtZero: true,
          stacked
        }
      }
    }

    return base
  }

  disconnect() {
    this.chart?.destroy()
  }
}
